import { Box, UnorderedList } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { VirtualListItem } from './VirtualListItem';
import { isDefined } from '@duke-hq/lib';
import type { SiteLocationType, SiteRegionType } from './SiteList';

export function VirtualizedSiteList({
  sites,
  region,
  parentRef,
  toggleSiteAvailability,
}: {
  sites: SiteLocationType[];
  region?: SiteRegionType | undefined;
  parentRef: React.RefObject<HTMLDivElement | null>;
  toggleSiteAvailability: (site: SiteLocationType) => void;
}) {
  const virtualizer = useVirtualizer({
    count: sites?.length,
    getScrollElement: () => {
      if (parentRef == null) {
        return null;
      }
      return parentRef.current;
    },
    estimateSize: useCallback(() => 1000, []),
  });

  const items = virtualizer.getVirtualItems();

  return (
    <Box height={`${virtualizer.getTotalSize()}px`} width={'full'} position="relative">
      <UnorderedList
        listStyleType="none"
        p={0}
        m={0}
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="auto"
        transform={`translateY(${items[0]?.start ?? 0}px)`}
      >
        {items
          .map((virtualRow) => {
            const site = sites[virtualRow.index];
            if (typeof site !== 'undefined') {
              return (
                <VirtualListItem
                  key={`${virtualRow.key}-${site.id}`}
                  data-index={virtualRow.index}
                  virtualRow={virtualRow}
                  site={site}
                  region={region}
                  measureElement={virtualizer.measureElement}
                  toggleSiteAvailability={toggleSiteAvailability}
                  isLastItem={virtualRow.index === sites.length - 1}
                />
              );
            }
          })
          .filter(isDefined)}
      </UnorderedList>
    </Box>
  );
}
